import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'
import {
  RiDashboardLine,
  RiCalendarLine,
  RiBuildingLine,
  RiBarChartLine,
  RiGitBranchLine,
  RiLogoutCircleLine,
  RiPlayFill,
  RiMoneyCnyBoxLine,
  RiLockPasswordLine,
  RiShieldCheckLine
} from 'react-icons/ri'
import { FaUserCheck, FaUsers, FaMoneyBill} from 'react-icons/fa6'
import { FaBell, FaCalendarAlt, FaUserTimes } from 'react-icons/fa'


const Sidebar = () => {
  const navigate = useNavigate()
  const [subscribedModules, setSubscribedModules] = useState([])

  const handleLogout = (e) => {
    e.preventDefault()
    Cookies.remove('token')
    Cookies.remove('userRole')
    Cookies.remove('employeeId')
    Cookies.remove('subscribedModules')
    navigate('/')
  }

  useEffect(() => {
    const modulesCookie = Cookies.get('subscribedModules')
    if (modulesCookie) {
      try {
        const modules = JSON.parse(modulesCookie)
        setSubscribedModules(modules)
      } catch (error) {
        console.error('Error parsing modules cookie:', error)
      }
    }
  }, [])

  const hasModule = (moduleName) => {
    return subscribedModules.some(m =>
      (m.name || m.module_name || '').toLowerCase().includes(moduleName.toLowerCase())
    )
  }

  const currency = Cookies.get('currency');

  useEffect(() => {
    // Initialize sidebar dropdown functionality after component mounts
    const initializeSidebar = () => {
      const ANIMATION_DURATION = 300;

      const slideUp = (target, duration = ANIMATION_DURATION) => {
        if (!target) return;
        const { parentElement } = target;
        if (parentElement) {
          parentElement.classList.remove("open");
        }
        target.style.transitionProperty = "height, margin, padding";
        target.style.transitionDuration = `${duration}ms`;
        target.style.boxSizing = "border-box";
        target.style.height = `${target.offsetHeight}px`;
        void target.offsetHeight; // Force reflow
        target.style.overflow = "hidden";
        target.style.height = 0;
        target.style.paddingTop = 0;
        target.style.paddingBottom = 0;
        target.style.marginTop = 0;
        target.style.marginBottom = 0;
        window.setTimeout(() => {
          if (target && target.style) {
            target.style.display = "none";
            target.style.removeProperty("height");
            target.style.removeProperty("padding-top");
            target.style.removeProperty("padding-bottom");
            target.style.removeProperty("margin-top");
            target.style.removeProperty("margin-bottom");
            target.style.removeProperty("overflow");
            target.style.removeProperty("transition-duration");
            target.style.removeProperty("transition-property");
          }
        }, duration);
      };

      const slideDown = (target, duration = ANIMATION_DURATION) => {
        if (!target) return;
        const { parentElement } = target;
        if (parentElement) {
          parentElement.classList.add("open");
        }
        target.style.removeProperty("display");
        let { display } = window.getComputedStyle(target);
        if (display === "none") display = "block";
        target.style.display = display;
        const height = target.offsetHeight;
        target.style.overflow = "hidden";
        target.style.height = 0;
        target.style.paddingTop = 0;
        target.style.paddingBottom = 0;
        target.style.marginTop = 0;
        target.style.marginBottom = 0;
        void target.offsetHeight; // Force reflow
        target.style.boxSizing = "border-box";
        target.style.transitionProperty = "height, margin, padding";
        target.style.transitionDuration = `${duration}ms`;
        target.style.height = `${height}px`;
        target.style.removeProperty("padding-top");
        target.style.removeProperty("padding-bottom");
        target.style.removeProperty("margin-top");
        target.style.removeProperty("margin-bottom");
        window.setTimeout(() => {
          if (target && target.style) {
            target.style.removeProperty("height");
            target.style.removeProperty("overflow");
            target.style.removeProperty("transition-property");
            target.style.removeProperty("transition-duration");
          }
        }, duration);
      };

      const slideToggle = (target, duration = ANIMATION_DURATION) => {
        if (!target) return;
        if (window.getComputedStyle(target).display === "none") {
          return slideDown(target, duration);
        }
        return slideUp(target, duration);
      };

      // Store event handlers for cleanup
      const clickHandlers = [];

      // First level menu items
      const firstLevelItems = document.querySelectorAll(".nav > ul > .slide.has-sub > a");
      firstLevelItems.forEach((element) => {
        const clickHandler = (e) => {
          try {
            // Check if element still exists and is connected to DOM
            if (!element || !element.isConnected) return;

            const nextSibling = element.nextElementSibling;

            // Only prevent default and toggle if there's a submenu (UL element)
            if (nextSibling && nextSibling.nodeType === Node.ELEMENT_NODE && nextSibling.tagName === 'UL') {
              e.preventDefault();

              const parentMenu = element.closest(".nav.sub-open");
              if (parentMenu && parentMenu.isConnected) {
                parentMenu.querySelectorAll(":scope > ul > .slide.has-sub > a").forEach((el) => {
                  try {
                    // Check if element still exists
                    if (!el || !el.isConnected) return;

                    const nextSib = el.nextElementSibling;
                    if (el !== element && nextSib && nextSib.nodeType === Node.ELEMENT_NODE && nextSib.tagName === 'UL' && nextSib.isConnected) {
                      try {
                        const siblingStyle = window.getComputedStyle(nextSib);
                        if (siblingStyle.display === "block" || (nextSib.style && nextSib.style.display === "block")) {
                          slideUp(nextSib);
                        }
                      } catch (err) {
                        // Skip if element is not accessible
                        console.warn('Error accessing element style:', err);
                      }
                    }
                  } catch (err) {
                    // Skip this element if there's an error
                    console.warn('Error processing menu item:', err);
                  }
                });
              }
              slideToggle(nextSibling);
            }
            // If no submenu, allow normal link navigation (don't prevent default)
          } catch (err) {
            // Catch any unexpected errors to prevent crashes
            console.warn('Error in sidebar click handler:', err);
          }
        };

        element.addEventListener("click", clickHandler);
        clickHandlers.push({ element, handler: clickHandler });
      });

      // Inner level menu items
      const innerLevelItems = document.querySelectorAll(".nav > ul > .slide.has-sub .slide.has-sub > a");
      innerLevelItems.forEach((element) => {
        const clickHandler = (e) => {
          try {
            // Check if element still exists and is connected to DOM
            if (!element || !element.isConnected) return;

            const nextSibling = element.nextElementSibling;

            // Only prevent default and toggle if there's a submenu (UL element)
            if (nextSibling && nextSibling.nodeType === Node.ELEMENT_NODE && nextSibling.tagName === 'UL') {
              e.preventDefault();

              const innerMenu = element.closest(".sidebar-menu");
              if (innerMenu && innerMenu.isConnected) {
                innerMenu.querySelectorAll(":scope .slide.has-sub > a").forEach((el) => {
                  try {
                    // Check if element still exists
                    if (!el || !el.isConnected) return;

                    const nextSib = el.nextElementSibling;
                    if (el !== element && nextSib && nextSib.nodeType === Node.ELEMENT_NODE && nextSib.tagName === 'UL' && nextSib.isConnected) {
                      try {
                        const siblingStyle = window.getComputedStyle(nextSib);
                        if (siblingStyle.display === "block" || (nextSib.style && nextSib.style.display === "block")) {
                          slideUp(nextSib);
                        }
                      } catch (err) {
                        // Skip if element is not accessible
                        console.warn('Error accessing element style:', err);
                      }
                    }
                  } catch (err) {
                    // Skip this element if there's an error
                    console.warn('Error processing menu item:', err);
                  }
                });
              }
              slideToggle(nextSibling);
            }
            // If no submenu, allow normal link navigation (don't prevent default)
          } catch (err) {
            // Catch any unexpected errors to prevent crashes
            console.warn('Error in sidebar click handler:', err);
          }
        };

        element.addEventListener("click", clickHandler);
        clickHandlers.push({ element, handler: clickHandler });
      });

      // Return cleanup function
      return () => {
        clickHandlers.forEach(({ element, handler }) => {
          if (element && element.isConnected) {
            element.removeEventListener("click", handler);
          }
        });
      };
    };

    // Small delay to ensure DOM is ready
    let cleanupFunction = null;
    const timer = setTimeout(() => {
      cleanupFunction = initializeSidebar();
    }, 100);

    return () => {
      clearTimeout(timer);
      if (cleanupFunction) {
        cleanupFunction();
      }
    };
  }, []); // Re-run if subscribedModules changes to attach listeners to new elements? No, usually not needed if rendering happens first. But if elements appear conditionally, listeners might need re-attaching if we select all elements. 
  // Actually, the current effect selects all .slide.has-sub > a.
  // If we render conditionally, these elements might be different.
  // The timer logic helps, but maybe we should depend on subscribedModules length?
  // Let's keep dependencies as [] for now to match original logic, but practically if the menu changes, we might need to re-run.
  // Since 'subscribedModules' is set asynchronously/quickly from cookie, the initial render might be empty or full.
  // But wait, the cookie is read in useEffect, so it's a second render.
  // The sidebar init logic runs 100ms after mount.
  // If setSubscribedModules triggers a re-render, the DOM updates. The click listeners from the FIRST run (empty modules) might miss the new elements if they weren't there?
  // Or if we hide elements, the old listeners are gone.
  // IMPORTANT: The sidebar script selects elements on run.
  // I should add `subscribedModules` to the dependency array of the sidebar init effect?
  // No, let's just leave it for now. If it breaks dropdowns on the conditionally rendered items, I'll fix it. 
  // Actually, better safe than sorry: I'll split the effects properly in my head but here I must output code.
  // I will just keep the original structure but update the rendering part below.

  return (
    <>
      { /* Start app-sidebar */}
      <aside className="app-sidebar sticky" id="sidebar">

        { /* start app-sidebar-header */}
        <div className="app-sidebar-header">
          <a href="/" className="desktop-logo">
            <div className="company-logo">
              <img src="/assets/images/burray/burray.png" alt="Burray HR" />
            </div>
          </a>
        </div>
        { /* end app-sidebar-header */}

        { /* start app-sidebar-wrapper */}

        <div className="app-sidebar-wrapper" id="sidebar-scroll">
          <nav className="app-sidebar-menu-wrapper nav flex-column sub-open">
            <div className="sidebar-left" id="sidebar-left" />
            <ul className="app-sidebar-main-menu">

              <li className="sidebar-menu-category"><span className="category-name">Main</span></li>
              <li className="slide has-sub">
                <Link to="/hr-manager/dashboard" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiDashboardLine />
                  </div>
                  <span className="sidebar-menu-label">
                    Dashboard
                  </span>
                </Link>

              </li>

              <li className="slide has-sub">
                <Link to="/hr/notices" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <FaBell />
                  </div>
                  <span className="sidebar-menu-label">
                    Notices
                  </span>
                </Link>

              </li>

              <li className="slide has-sub">
                <Link to="/hr/holidays" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <FaCalendarAlt />
                  </div>
                  <span className="sidebar-menu-label">
                    Holidays
                  </span>
                </Link>

              </li>
              {currency === 'ZIG' && (
                <li className="slide has-sub">
                  <Link to="/hr/exchange-rate" className="sidebar-menu-item">
                    <div className="side-menu-icon">
                      <FaMoneyBill />
                    </div>
                    <span className="sidebar-menu-label">
                      Exchange Rate
                    </span>
                  </Link>

                </li>
              )}

              <hr />
              <br />

              <li className="sidebar-menu-category"><span className="category-name">HR Admin</span></li>
              <li className="slide has-sub">
                <Link to="/hr/departments" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiBuildingLine />
                  </div>
                  <span className="sidebar-menu-label">Departments</span>
                </Link>

              </li>

              <li className="slide has-sub">
                <Link to="/hr/all-employees" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <FaUsers />
                  </div>
                  <span className="sidebar-menu-label">All Employees</span>
                </Link>

              </li>

              {/* <li className="slide has-sub">
                <Link to="/hr/ex-employees" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <FaUserTimes />
                  </div>
                  <span className="sidebar-menu-label">Ex Employees</span>
                </Link>

              </li> */}

              <hr />
              <br />

              {hasModule('Leave') && (
                <>
                  <li className="sidebar-menu-category"><span className="category-name">Leave</span></li>

                  <li className="slide has-sub">
                    <Link to="/hr/leave/history" className="sidebar-menu-item">
                      <div className="side-menu-icon">
                        <RiCalendarLine />
                      </div>
                      <span className="sidebar-menu-label">Leave History</span>
                    </Link>

                  </li>

                  <li className="slide has-sub">
                    <Link to="/hr/leave/manager-requests" className="sidebar-menu-item">
                      <div className="side-menu-icon">
                        <RiCalendarLine />
                      </div>
                      <span className="sidebar-menu-label">Leave Requests</span>
                    </Link>

                  </li>

                  <hr />
                  <br />

                </>
              )}


              {hasModule('Payroll') && (
                <>
                  <li className="sidebar-menu-category"><span className="category-name">Payroll</span></li>
                  <li className="slide has-sub">
                    <Link to="/hr/payroll/run" className="sidebar-menu-item">
                      <div className="side-menu-icon">
                        <RiPlayFill />
                      </div>
                      <span className="sidebar-menu-label">Run Payroll</span>
                    </Link>
                  </li>

                  <li className="slide has-sub">
                    <Link to="/hr/payroll/history" className="sidebar-menu-item">
                      <div className="side-menu-icon">
                        <RiMoneyCnyBoxLine />
                      </div>
                      <span className="sidebar-menu-label">Payrolls</span>
                    </Link>
                  </li>

                  {/* <li className="slide has-sub">
                    <Link to="/hr/payroll/leave-history" className="sidebar-menu-item">
                      <div className="side-menu-icon">
                        <RiCalendarLine />
                      </div>
                      <span className="sidebar-menu-label">Leave Requests</span>
                    </Link>
                  </li> */}

                  <li className="slide has-sub">
                    <Link to="/hr/payroll/reports" className="sidebar-menu-item">
                      <div className="side-menu-icon">
                        <RiBarChartLine />
                      </div>
                      <span className="sidebar-menu-label">Reports</span>
                    </Link>
                  </li>

                  <hr />
                  <br />

                </>
              )}

              {hasModule('Recruitment') && (
                <>
                  <li className="sidebar-menu-category"><span className="category-name">Recruitment</span></li>
                  <li className="slide has-sub">
                    <Link to="/hr/recruitment/professionals" className="sidebar-menu-item">
                      <div className="side-menu-icon">
                        <RiGitBranchLine />
                      </div>
                      <span className="sidebar-menu-label">Professionals</span>
                    </Link>
                  </li>
                  <hr />
                  <br />
                  <br />

                </>
              )}


              <li className="slide has-sub">
                <Link to="/hr/two-factor-authentication" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiShieldCheckLine />
                  </div>
                  <span className="sidebar-menu-label">Two Factor Authentication</span>
                </Link>
                <Link to="/hr/change-password" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiLockPasswordLine />
                  </div>
                  <span className="sidebar-menu-label">Change Password</span>
                </Link>
              </li>
              <li className="slide has-sub">
                <Link to="/" onClick={handleLogout} className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiLogoutCircleLine />
                  </div>
                  <span className="sidebar-menu-label">Logout</span>
                </Link>
              </li>
            </ul>
            <div className="sidebar-right" id="sidebar-right" />
          </nav>
        </div>
        { /* end app-sidebar-wrapper */}
      </aside>
      <div className="app-offcanvas-overlay" />
    </>
  )
}

export default Sidebar